import './AreaEditPage.css';

import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Banner from '../../b2b-common/Banner';
import { LoginContext } from '../../../App';
import { Edit, EditOff } from '@mui/icons-material';
import Frame from '../../b2b-common/Frame';
import EditText from '../../b2b-common/EditText';
import { generateSelectField, handleObjectFieldChange } from '../../b2b-common/helpers';
import BooleanIconToggle from '../../b2b-common/BooleanIconToggle';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { deleteAreaImg, saveNewArea, updateArea, uploadAreaImage } from '../../../services/b2b-plans-apiService';
import { showNotification } from '../../b2b-common/Notification';
import { B2BContentContext } from '../../layout/Layout';
import CustomButton from '../../b2b-common/CustomButton';
import B2BImageCard from '../../b2b-common/B2BImageCard';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';

const AreaEditPage = () => {

    const { t } = useB2BTranslation('b2b-plans');
    const { setNotification } = useContext(B2BContentContext);
    const navigate = useNavigate();
    const location = useLocation();
    // console.log('location:', location);
    const { plan } = location.state || {};
    const { area } = location.state || {};
    const returnTo = location.state?.from || '/plans/plans-explorer';

    // console.log('returnTo:', returnTo);

    const { loginData } = useContext(LoginContext);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [areaImage, setAreaImage] = useState(null);
    const [deleteAreaImage, setDeleteAreaImage] = useState(false);

    // plan should be passed as a prop otherwise an error will be thrown
    // if (!plan) {
    //     navigate(from);
    //     return null
    // }

    const initialArea = area ? {
        ...area,
        areaStartDate: dayjs(area.areaStartDate).startOf('day'),
        areaEndDate: dayjs(area.areaEndDate).startOf('day'),
    } : {
        areaName: t('New area'),
        areaDescription: t('Area description'),
        readOnly: false,
        areaStatus: 'IDEA',
        areaStartDate: dayjs(plan.planStartDate).startOf('day'),
        areaEndDate: dayjs(plan.planEndDate).startOf('day'),
        imagePath: null,
    };
    // console.log('initialArea:', initialArea);

    const [currentArea, setCurrentArea] = useState(initialArea);
    // console.log('currentArea:', currentArea);

    const handleSave = async () => {
        const areaToSave = {
            ...currentArea,
            areaStartDate: currentArea.areaStartDate.format('YYYY-MM-DD'),
            areaEndDate: currentArea.areaEndDate.format('YYYY-MM-DD'),
        };
        // console.log('areaToSave:', areaToSave);
        if (plan && plan.planUUID) {
            if (area && area.areaId > 0) {
                // console.log('Update area:', areaToSave);
                try {
                    setIsSaving(true);
                    await updateArea(loginData.accessToken, plan.planUUID, areaToSave);
                    if (areaImage) {
                        const updatedArea = await uploadAreaImage(loginData.accessToken, plan.planUUID, area.areaId, areaImage);
                    } else if (deleteAreaImage) {
                        const deleteResponse = await deleteAreaImg(loginData.accessToken, plan.planUUID, area.areaId);
                    }
                    showNotification(setNotification, t('Area updated'), 'success', 3000);
                    navigate(`${returnTo}?selectedAreaId=${area.areaId}`);
                } catch (error) {
                    setError(error.message);
                    console.error('Error saving area:', error);
                    showNotification(setNotification, t('Error saving area: ') + error.message, 'error', 3000);
                } finally {
                    setIsSaving(false);
                }
            } else {
                // console.log('Save new area:', areaToSave);
                try {
                    setIsSaving(true);
                    const newArea = await saveNewArea(loginData.accessToken, plan.planUUID, areaToSave);
                    if (areaImage) {
                        await uploadAreaImage(loginData.accessToken, plan.planUUID, newArea.areaId, areaImage);
                    }
                    showNotification(setNotification, t('Area saved'), 'success', 3000);
                    navigate(`${returnTo}?selectedAreaId=${newArea.areaId}`);
                } catch (error) {
                    setError(error.message);
                    console.error('Error saving area:', error);
                    showNotification(setNotification, t('Error saving area: ') + error.message, 'error', 3000);
                } finally {
                    setIsSaving(false);
                }
            }
        } else {
            console.error('No plan data found.');
            showNotification(setNotification, t('No plan data found.'), 'info', 3000);
        }
    }

    const areaStatusOptions = [
        { value: 'IDEA', label: t('Idea') },
        { value: 'PENDING_APPROVAL', label: t('Pending Approval') },
        { value: 'APPROVED', label: t('Approved') },
    ];

    return (
        <div className='area-edit-page-container'>
            <Banner message={area ? area.areaName : t('New area')} underline={false} />
            <div className='area-edit-page-fields'>
                <div className='area-edit-page-fields-column-left'>
                    <Frame title='Area details' className='area-edit-page-details-frame transparent-no-title-theme'>
                        <div className='area-edit-page-details'>
                            <div className='area-edit-page-details-row'>
                                <EditText object={currentArea} objectField={'areaName'} setObjectField={setCurrentArea} label={t('Area name')} />
                                {generateSelectField(areaStatusOptions,
                                    {
                                        label: t('Area status'),
                                        value: currentArea.areaStatus,
                                        onChange: (e) => handleObjectFieldChange('areaStatus', e.target.value, setCurrentArea),
                                        className: 'area-edit-page-status-field',
                                    }
                                )}
                                <BooleanIconToggle
                                    value={currentArea.readOnly}
                                    onChange={(value) => handleObjectFieldChange('readOnly', value, setCurrentArea)}
                                    trueIcon={<EditOff />}
                                    trueText={t('Read only')}
                                    falseIcon={<Edit />}
                                    falseText={t('Edit mode')}
                                    className='area-edit-page-read-only-toggle'
                                />
                            </div>
                            <div className='area-edit-page-details-row'>
                                <EditText object={currentArea} objectField={'areaDescription'} setObjectField={setCurrentArea} label={t('Area description')} multiline={true} rows={10} />
                            </div>
                        </div>
                    </Frame>
                </div>
                <div className='area-edit-page-fields-column-right'>
                    <Frame title={t('Area dates')} className='area-edit-page-dates-frame transparent-no-title-theme'>
                        <div className='area-edit-page-dates'>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
                                <DatePicker
                                    label={t('Area start date')}
                                    value={currentArea.areaStartDate}
                                    onChange={(date) => handleObjectFieldChange('areaStartDate', date, setCurrentArea)}
                                    slotProps={{ textField: { variant: 'standard' } }}
                                    minDate={dayjs(plan.planStartDate)}
                                    maxDate={dayjs(plan.planEndDate)}
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
                                <DatePicker
                                    label={t('Area end date')}
                                    value={currentArea.areaEndDate}
                                    onChange={(date) => handleObjectFieldChange('areaEndDate', date, setCurrentArea)}
                                    slotProps={{ textField: { variant: 'standard' } }}
                                    minDate={dayjs(plan.planStartDate)}
                                    maxDate={dayjs(plan.planEndDate)}
                                />
                            </LocalizationProvider>
                        </div>
                    </Frame>
                    <Frame title='Area media' className='area-edit-page-media-frame  transparent-no-title-theme'>
                        <B2BImageCard
                            imagePath={currentArea.imagePath}
                            showChangeButton={true}
                            requestChangeConfirmation={true}
                            onChange={(file) => setAreaImage(file)}
                            showDeleteButton={true}
                            requestDeleteConfirmation={true}
                            onDelete={() => setDeleteAreaImage(true)}
                            synchrousUpdate={false}
                            className='area-edit-page-image-card'
                        />                            
                    </Frame>
                </div>
            </div>
            <div className='area-edit-page-buttons'>
                <CustomButton
                    label={t('Save')}
                    onClick={handleSave}
                />
                <CustomButton
                    label={t('Cancel')}
                    onClick={() => navigate(returnTo)}
                    className={'white_02'}
                />
            </div>
        </div>
    );
};

export default AreaEditPage;